import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { MFA_SETUP_PREFIX, type Spec, type SpecOverride, clockOf } from "@baros/core";
import { actingOrg, setActingOrg } from "./acting.js";
import { api, ApiError, watchSession, type Library, type Me, type VenueState } from "./api.js";
import { currentSession, refreshSession, secondsToExpiry, signOut, type Session } from "./auth.js";
import type { ProposedBuild } from "./Chat.js";
import { AppContext, type AppState, type ScreenState } from "./context.js";
import { installErrorReporter } from "./error-reporter.js";
import { themeForNow, setVenueZone } from "./format.js";
import { Icon } from "./icons.js";
import { Nav, type NavPanel } from "./Nav.js";
import { HOME, locationToUrl, screenToUrl, urlToLocation, type AppLocation } from "./routes.js";
import { DEFAULT_SCREEN, SCREENS } from "./screens.js";
import { SignIn } from "./SignIn.js";
import { AccountMfa } from "./AccountMfa.js";
import { hasFeature } from "./features.js";
import { AccountTablet } from "./AccountTablet.js";
import { LockScreen, type Unlocked } from "./LockScreen.js";
import { StepUp } from "./StepUp.js";
import {
  dropElevation,
  LOCKED_EVENT,
  setTabletSession,
  STEP_UP_EVENT,
  tabletApi,
  tabletKey,
  tabletSession,
  watchTabletResponses,
} from "./tablet.js";

/** The build, stamped by CI as yyyy.mm.<PR>; "dev" when run locally. */
const VERSION: string = import.meta.env.VITE_APP_VERSION || "dev";

/** What the address bar names, or the dashboard when it names nothing. */
/**
 * Whether this person has to set up two-step sign-in before anything else
 * (#92). Only when Cognito could be asked: a lookup that failed says
 * nothing either way, and the API enforces the rule regardless.
 */
function mfaSetupDue(m: Me): boolean {
  return m.mfa?.required === true && m.mfa.confirmed && m.mfa.method === null;
}

function readLocation(): AppLocation {
  return urlToLocation(window.location.pathname, window.location.search) ?? { screen: DEFAULT_SCREEN };
}

const here = () => window.location.pathname + window.location.search;

const COLLAPSED_KEY = "baros.menuCollapsed";
/** Where the full sidebar starts; below it the menu is always the rail. */
const WIDE = "(min-width: 1181px)";

/** Whether the screen is wide enough for the sidebar, following resizes and rotation. */
function useWide(): boolean {
  const [wide, setWide] = useState(() => window.matchMedia(WIDE).matches);
  useEffect(() => {
    const query = window.matchMedia(WIDE);
    const onChange = () => setWide(query.matches);
    // Both: a media query's change event is the precise one, and a resize
    // is the one every browser and emulator is sure to send. Missing it
    // would leave a tablet showing the desktop sidebar.
    query.addEventListener("change", onChange);
    window.addEventListener("resize", onChange);
    return () => {
      query.removeEventListener("change", onChange);
      window.removeEventListener("resize", onChange);
    };
  }, []);
  return wide;
}

export function App() {
  const [session, setSession] = useState<Session | null | undefined>(undefined);
  const [me, setMe] = useState<Me | null>(null);
  const [library, setLibrary] = useState<Library | null>(null);
  const [venueState, setVenueState] = useState<VenueState | null>(null);
  /**
   * The address the page was opened at, read once and before anything asks
   * the API for anything — before sign-in, too, so a signed-out visitor
   * following a link lands on it once they are in (#124).
   *
   * An org in the link becomes the acting org for the tab, which every
   * request then sends as `x-baros-org`. The API decides whether the person
   * may act there and refuses with a 403 if not; that refusal is the check,
   * not anything here.
   */
  const [opened] = useState<AppLocation>(() => {
    const location = readLocation();
    if (location.orgId) setActingOrg(location.orgId);
    return location;
  });
  const [screen, setScreen] = useState<ScreenState>(opened.screen);
  /** The venue the address asked for; used if the person is on it. */
  const [wantedVenue, setWantedVenue] = useState<string | undefined>(opened.venueId);
  /** Set when `/v1/me` refused the organisation the link named. */
  const [refusal, setRefusal] = useState<{ readonly orgId: string; readonly message: string } | null>(null);
  /**
   * Bumped on every real navigation and used in the screen's key, so the
   * screen mounts afresh. Screens read their params once, when they mount;
   * without this, back from /menu?specId=negroni to /menu would keep the
   * Negroni selected. A `replace` leaves it alone, which is the point of one.
   */
  const [visit, setVisit] = useState(0);
  const [draftBuild, setDraftBuild] = useState<ProposedBuild | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [theme, setTheme] = useState<"dark" | "light">(themeForNow());
  /** What the rail or bottom bar has open over the screen. */
  const [panel, setPanel] = useState<NavPanel>(null);
  /**
   * Whether the screen is wide enough for the full sidebar, and whether the
   * person folded it away anyway, remembered per browser. Either way short
   * of the sidebar, the menu is the tablet's rail (`.app.rail`).
   */
  const wide = useWide();
  const [collapsed, setCollapsed] = useState<boolean>(() => {
    try {
      return window.localStorage.getItem(COLLAPSED_KEY) === "1";
    } catch {
      return false;
    }
  });
  const collapse = (value: boolean) => {
    setCollapsed(value);
    setPanel(null);
    try {
      window.localStorage.setItem(COLLAPSED_KEY, value ? "1" : "0");
    } catch {
      // Storage blocked: the choice lasts until the page is reloaded.
    }
  };
  /** The organisation an operator opened from the platform screen, if any. */
  const acting = actingOrg();

  useEffect(() => {
    document.documentElement.setAttribute("data-theme", theme);
  }, [theme]);

  /** Locked over a screen in use, rather than come up locked (#80). */
  const [tabletLocked, setTabletLocked] = useState(false);
  /** On a tablet, a password sign-in instead of the PIN screen. */
  const [passwordInstead, setPasswordInstead] = useState(false);
  /** An action wants a full sign-in. */
  const [stepUp, setStepUp] = useState(false);
  /** Who held the tablet when it locked, so the same person picks up where they were. */
  const lastHolder = useRef<string | null>(null);

  // A proper sign-in wins — a manager setting up, or working on, a tablet.
  // Otherwise an enrolled tablet with a live unlock acts as whoever
  // unlocked it, and one without comes up on its PIN screen.
  useEffect(() => {
    watchTabletResponses();
    currentSession().then((s) => {
      if (s) return setSession(s);
      const key = tabletKey();
      const held = tabletSession();
      setSession(key && held ? { email: held.name, accessToken: `Device ${key.token} ${held.token}` } : null);
    });
  }, []);

  /**
   * The org and venue this render is at, for `go()` and the back button to
   * build and read addresses with. A ref so that `go` keeps one identity:
   * screens list it in effect dependencies.
   */
  const where = useRef<{ orgId: string | undefined; venueId: string | undefined }>({
    orgId: undefined,
    venueId: undefined,
  });

  // Back and forward. The address has already changed; follow it.
  useEffect(() => {
    const onPop = () => {
      const location = readLocation();
      // Another organisation is another boot: its library, its roles, its
      // venues. The address names it, so loading the page again opens it.
      if (location.orgId && where.current.orgId && location.orgId !== where.current.orgId) {
        window.location.reload();
        return;
      }
      if (location.venueId) setWantedVenue(location.venueId);
      setScreen(location.screen);
      setVisit((n) => n + 1);
    };
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, []);

  const go = useCallback((next: ScreenState, options?: { readonly replace?: boolean }) => {
    const url = locationToUrl({ ...where.current, screen: next });
    if (options?.replace) {
      if (url !== here()) window.history.replaceState(null, "", url);
    } else {
      // Re-clicking where you already are refreshes the screen but does not
      // add a second identical entry to step back through.
      if (url !== here()) window.history.pushState(null, "", url);
      setVisit((n) => n + 1);
    }
    setScreen(next);
  }, []);

  /**
   * A token the API will not take, after the client has already tried to
   * refresh it. On a tablet that is the PIN screen; anywhere else it is the
   * sign-in screen. Until this was registered, every screen caught its own
   * 401 and printed it, which left somebody looking at a dashboard of
   * "Token rejected" with no way on but a reload.
   */
  const endSession = useCallback((token: string) => {
    if (token.startsWith("Device ")) setTabletSession(null);
    else signOut();
    setSession(null);
  }, []);

  // Every call the app makes goes through `request` in api.ts, which hands
  // a refreshed token back here so the screens get it, and says when the
  // session is over. Registered for as long as the app is mounted.
  useEffect(() => {
    watchSession({
      renew: async () => {
        const fresh = await refreshSession();
        if (fresh) setSession(fresh);
        return fresh?.accessToken ?? null;
      },
      onEnded: endSession,
    });
    return () => watchSession(null);
  }, [endSession]);

  // Access tokens last an hour and a tablet stays open all service. Every
  // five minutes, if the token has under fifteen minutes left, refresh it
  // with the refresh token and hand the new one to every screen through the
  // context — before a request can fail, not after.
  useEffect(() => {
    // A tablet's unlock is ours, not Cognito's, and has nothing to refresh.
    if (!session || session.accessToken.startsWith("Device ")) return;
    const tick = () => {
      if (secondsToExpiry(session.accessToken) > 15 * 60) return;
      refreshSession().then((s) => {
        if (s) setSession(s);
      });
    };
    tick();
    const timer = window.setInterval(tick, 5 * 60 * 1000);
    return () => window.clearInterval(timer);
  }, [session]);

  /**
   * The venue this session acts at, or null: the one the address names if
   * the person is on it, otherwise the first they are on. A link to a venue
   * they are not on opens their own instead, and the address is rewritten to
   * say so — the API would refuse the other venue's data anyway.
   *
   * No fallback to `venues[0]`. That handed a member assigned to no venue
   * one they are not on, which every read then 403s on, and the whole app
   * came up as an error page rather than as a screen saying they are not on
   * a venue yet. An org owner or admin needs no fallback: `widenAdminVenues`
   * puts every venue in the org on their claims, so `assigned` is already
   * true for all of them.
   */
  const assignedVenues = useMemo(() => me?.venues.filter((v) => v.assigned) ?? [], [me]);
  const venueId = useMemo(
    () =>
      assignedVenues.find((v) => v.venueId === wantedVenue)?.venueId ??
      assignedVenues[0]?.venueId ??
      null,
    [assignedVenues, wantedVenue],
  );
  where.current = { orgId: me?.org.orgId, venueId: venueId ?? undefined };

  const token = session?.accessToken ?? "";

  // Uncaught errors go to the API with the screen and venue they happened
  // on (#88). Installed once; the ref is how it reads where the app is at
  // the moment of the error rather than where it was when installed.
  const reporting = useRef({ token, screen: screen.kind, venueId });
  reporting.current = { token, screen: screen.kind, venueId };
  useEffect(() => installErrorReporter(() => reporting.current), []);

  const refreshLibrary = useCallback(async () => {
    if (!token) return;
    setLibrary(await api.library(token));
  }, [token]);

  const refreshVenue = useCallback(async () => {
    if (!token || !venueId) return;
    setVenueState(await api.venueState(token, venueId));
  }, [token, venueId]);

  const load = useCallback(async (s: Session) => {
    setError(null);
    try {
      const m = await api.me(s.accessToken);
      setMe(m);
      // Due to set up two-step sign-in: every other request would be
      // refused, and the screen that does it needs only `me` (#92).
      if (mfaSetupDue(m)) return;
      setLibrary(await api.library(s.accessToken));
    } catch (err) {
      // `request` has already tried a refresh and told `endSession`; this
      // only stops `load` carrying on as though it had data.
      if (err instanceof ApiError && err.status === 401) return;
      // The organisation the link named would not have this person.
      const named = actingOrg();
      if (err instanceof ApiError && err.status === 403 && named && !err.message.startsWith(MFA_SETUP_PREFIX)) {
        setRefusal({ orgId: named, message: err.message });
        return;
      }
      setError(err instanceof Error ? err.message : "Could not load the library");
    }
  }, []);

  useEffect(() => {
    if (session) void load(session);
  }, [session, load]);

  // Every time on screen is the venue's own, wherever the person looking is.
  // Set while rendering, not after: the screens below format their times in
  // this same render, and an effect would leave the first paint in the
  // default zone. Idempotent, so rendering twice changes nothing.
  setVenueZone(clockOf(venueState?.venue).timeZone);

  // The venue's state follows the venue, which the address can change.
  const hasToken = token !== "";
  useEffect(() => {
    if (!token || !venueId) {
      setVenueState(null);
      return;
    }
    let live = true;
    api
      .venueState(token, venueId)
      .then((v) => live && setVenueState(v))
      .catch((err: unknown) => live && setError(err instanceof Error ? err.message : "Could not load the venue"));
    return () => {
      live = false;
    };
    // On the venue, and on there being a token at all: the token itself
    // refreshes every hour and the venue's state does not need to.
  }, [venueId, hasToken]);

  // Once the org and venue are known, the address says them in full: a link
  // from before they led the path, or to a venue the person is not on, is
  // rewritten to what is actually showing.
  useEffect(() => {
    if (!me) return;
    const url = locationToUrl({ orgId: me.org.orgId, venueId: venueId ?? undefined, screen });
    if (url !== here()) window.history.replaceState(null, "", url);
  }, [me, venueId, screen]);

  const replaceSpec = useCallback((spec: Spec) => {
    setLibrary((lib) =>
      lib
        ? {
            ...lib,
            specs: lib.specs.some((s) => s.specId === spec.specId)
              ? lib.specs.map((s) => (s.specId === spec.specId ? spec : s))
              : [...lib.specs, spec],
          }
        : lib,
    );
  }, []);

  const overrides = useMemo(
    () => new Map<string, SpecOverride>((venueState?.specOverrides ?? []).map((o) => [o.specId, o])),
    [venueState],
  );

  /**
   * Whether a screen is on offer: the organisation has its feature (D14),
   * and the person's role lets them use it.
   */
  const shown = (def: (typeof SCREENS)[number]) =>
    (!def.feature || hasFeature(me, def.feature)) && (!def.visible || (me !== null && def.visible(me)));

  /**
   * The screen to show. A screen the person may not use falls back to the
   * dashboard, as an unknown address does (#124) — the API refuses its data
   * either way, and an error page for following a link is worse than home.
   * Nothing is decided until `/v1/me` says what they may use.
   */
  const allowed = (kind: string) => {
    const def = SCREENS.find((s) => s.kind === kind);
    return def !== undefined && shown(def);
  };
  const refused = me !== null && !allowed(screen.kind);
  useEffect(() => {
    if (refused) go(DEFAULT_SCREEN, { replace: true });
  }, [refused, go]);

  const def = SCREENS.find((s) => s.kind === (refused ? DEFAULT_SCREEN.kind : screen.kind)) ?? SCREENS[0]!;

  // ------------------------------------------------------------ tablets (#80)
  const onTablet = session?.accessToken.startsWith("Device ") === true;

  /** Sign out of a proper sign-in, back to the sign-in screen at the root. */
  const signOutNow = () => {
    signOut();
    setPasswordInstead(false);
    setActingOrg(null);
    setSession(null);
    setMe(null);
    setLibrary(null);
    setVenueState(null);
    window.history.replaceState(null, "", "/");
    setScreen(DEFAULT_SCREEN);
  };

  /**
   * Lock, keeping this person's place: the address goes to the server
   * against them and this tablet, and their next unlock here goes back to
   * it. The screen stays mounted under the lock, so the same person coming
   * straight back finds it exactly as they left it.
   */
  const lockTablet = useCallback(async () => {
    const held = tabletSession();
    if (held) {
      lastHolder.current = held.userId;
      await tabletApi.lock(here()).catch(() => undefined);
    }
    setTabletSession(null);
    dropElevation();
    setPanel(null);
    setTabletLocked(true);
  }, []);

  /**
   * An unlock. The same person who locked it, straight back: lift the lock
   * and leave the screen alone. Anybody else, or a tablet that came up
   * locked: open where that person last locked this tablet, freshly loaded
   * in their name, so nobody inherits someone else's half-done screen.
   */
  const unlocked = (u: Unlocked, overlaid: boolean) => {
    setTabletSession(u.session);
    const key = tabletKey();
    if (overlaid && key && u.session.userId === lastHolder.current) {
      setSession({ email: u.session.name, accessToken: `Device ${key.token} ${u.session.token}` });
      setTabletLocked(false);
      return;
    }
    window.location.replace(u.resume ?? (overlaid ? "/" : here()));
  };

  // The server says the unlock lapsed; or an action wants a password.
  useEffect(() => {
    const onLocked = () => {
      if (tabletSession()) void lockTablet();
    };
    const onStepUp = () => setStepUp(true);
    window.addEventListener(LOCKED_EVENT, onLocked);
    window.addEventListener(STEP_UP_EVENT, onStepUp);
    return () => {
      window.removeEventListener(LOCKED_EVENT, onLocked);
      window.removeEventListener(STEP_UP_EVENT, onStepUp);
    };
  }, [lockTablet]);

  // Idle: lock after the organisation's time with nobody touching it, and
  // keep the server's unlock alive while somebody is — typing a long count
  // makes no requests, and must not come back locked when they press save.
  useEffect(() => {
    if (!onTablet || tabletLocked) return;
    const idle = tabletSession()?.idleMinutes ?? null;
    let last = Date.now();
    let pinged = Date.now();
    const touch = () => {
      last = Date.now();
      if (last - pinged > 60_000) {
        pinged = last;
        void tabletApi.ping().catch(() => undefined);
      }
    };
    const events = ["pointerdown", "keydown", "touchstart", "wheel"] as const;
    for (const e of events) window.addEventListener(e, touch, { passive: true });
    const timer = idle === null ? 0 : window.setInterval(() => {
      if (Date.now() - last > idle * 60_000) void lockTablet();
    }, 10_000);
    return () => {
      for (const e of events) window.removeEventListener(e, touch);
      if (timer) window.clearInterval(timer);
    };
  }, [onTablet, tabletLocked, lockTablet]);

  // The tab's title says where it is, so a row of tabs can be told apart.
  const subjectName =
    screen.kind === "recipes" && screen.params?.specId
      ? library?.specs.find((s) => s.specId === screen.params?.specId)?.name
      : undefined;
  useEffect(() => {
    document.title = [subjectName, def.kind === DEFAULT_SCREEN.kind ? undefined : def.label, "Bar OS"]
      .filter(Boolean)
      .join(" · ");
  }, [subjectName, def]);

  if (session === undefined) {
    return <div className="signin"><div className="label">Loading…</div></div>;
  }
  if (!session) {
    if (tabletKey() && !passwordInstead) {
      return <LockScreen onUnlocked={(u) => unlocked(u, false)} onPassword={() => setPasswordInstead(true)} />;
    }
    return (
      <>
        <SignIn onSignedIn={setSession} />
        {tabletKey() && (
          <button className="btn sm lock-back" onClick={() => setPasswordInstead(false)}>
            ← Back to PINs
          </button>
        )}
      </>
    );
  }
  if (refusal) {
    return (
      <div className="signin">
        <div className="msg err" style={{ maxWidth: 480 }}>
          <div style={{ fontWeight: 600 }}>This link is for an organisation you are not in.</div>
          <div style={{ marginTop: 6 }}>
            It points at <span className="mono">{refusal.orgId}</span>. {refusal.message}
          </div>
          <button
            className="btn"
            style={{ marginTop: 14 }}
            onClick={() => {
              setActingOrg(null);
              window.location.assign("/");
            }}
          >
            Go to my organisation →
          </button>
        </div>
      </div>
    );
  }

  // The organisation requires two-step sign-in of this person and they have
  // not set it up: this, and nothing else, until they have (#92).
  if (me && !onTablet && mfaSetupDue(me)) {
    return (
      <div className="signin">
        <div className="mfa-gate">
          <div>
            <div className="brand">Bar OS</div>
            <div className="label" style={{ marginTop: 6 }}>One more step</div>
          </div>
          <div className="muted" style={{ fontSize: 14, lineHeight: 1.5 }}>
            {me.org.name} asks everyone in your role to sign in with a second step as well as a password. Set it up
            once, here; after that Bar OS asks for it each time you sign in.
          </div>
          <AccountMfa token={token} method={null} required onChanged={() => void load(session)} />
          <button className="btn" onClick={signOutNow}>
            Sign out
          </button>
        </div>
      </div>
    );
  }

  const Screen = def.component;

  const state: AppState | null =
    me && library
      ? {
          token,
          email: session.email,
          me,
          library,
          venueState,
          venueId,
          overrides,
          screen,
          go,
          refreshLibrary,
          refreshVenue,
          replaceSpec,
          draftBuild,
          setDraftBuild,
        }
      : null;

  const venueRole = venueId ? me?.venues.find((v) => v.venueId === venueId)?.role : null;

  // A gated screen stays out of the nav until `me` says the person may use
  // it. Showing everything while `me` was still null put Platform in front of
  // every customer during the load, and for good when /v1/me failed.
  const visibleScreens = SCREENS.filter(shown);

  const place = [venueState?.venue.name, venueState?.venue.suburb].filter(Boolean).join(" · ");
  const account = (
    <>
      {me && me.orgs.length > 1 && !acting && (
        // Somebody who belongs to more than one organisation. Choosing
        // one sets it as the acting org for this tab, like an operator
        // opening a customer, and reloads.
        <select
          className="org-switch"
          aria-label="Organisation"
          value={me.org.orgId}
          onChange={(e) => {
            setActingOrg(e.target.value);
            // The address is the old organisation's — its recipe, its
            // order — so the new one starts at home.
            window.location.assign(locationToUrl({ orgId: e.target.value, screen: HOME }));
          }}
        >
          {me.orgs.map((o) => (
            <option key={o.orgId} value={o.orgId}>{o.name}</option>
          ))}
        </select>
      )}
      {assignedVenues.length > 1 && venueId && (
        // Somebody on more than one venue. The venue is in the address,
        // so this is a navigation like any other: back returns to the
        // venue before. The screen keeps its kind and drops its params,
        // which belonged to the other venue — its order, its tab.
        <select
          className="org-switch"
          aria-label="Venue"
          value={venueId}
          onChange={(e) => {
            const next = e.target.value;
            const url = locationToUrl({ orgId: me?.org.orgId, venueId: next, screen: { kind: screen.kind } });
            window.history.pushState(null, "", url);
            setVenueState(null);
            setWantedVenue(next);
            setScreen({ kind: screen.kind });
            setVisit((n) => n + 1);
            setPanel(null);
          }}
        >
          {assignedVenues.map((v) => (
            <option key={v.venueId} value={v.venueId}>{String(v.name ?? v.venueId)}</option>
          ))}
        </select>
      )}
      <span className="label" style={{ lineHeight: 1.6 }}>
        {onTablet ? `On bar: ${session.email}` : session.email}
        <br />
        {me?.org.role ?? ""}
        {venueRole ? ` / ${venueRole}` : ""}
      </span>
      {/* Venox operators too: they can reach every organisation, so they are
          the accounts that most need a second step (#92). */}
      {me?.mfa && !onTablet && (
        <AccountMfa
          token={token}
          method={me.mfa.method}
          required={me.mfa.required}
          onChanged={() => void (session && load(session))}
        />
      )}
      {me && !onTablet && hasFeature(me, "tablets") && (
        <AccountTablet
          token={token}
          orgId={me.org.orgId}
          venueId={venueId}
          venueName={venueState?.venue.name ?? "this venue"}
          pinSet={me.pinSet === true}
          canEnrol={venueId !== null && me.can["device:enrol"]}
          onPinChanged={() => void (session && load(session))}
        />
      )}
      <div className="row" style={{ gap: 6 }}>
        <button className="btn sm" onClick={() => setTheme(theme === "dark" ? "light" : "dark")}>
          {theme === "dark" ? "Light" : "Dark"}
        </button>
        {onTablet ? (
          <button className="btn sm" onClick={() => { setPanel(null); void lockTablet(); }}>
            Lock
          </button>
        ) : (
        <button className="btn sm" onClick={signOutNow}>
          Sign out
        </button>
        )}
      </div>
      <span className="version" title="Build">{VERSION}</span>
    </>
  );

  return (
    <div className={`app${!wide || collapsed ? " rail" : ""}`}>
      {/* Under a tablet's lock the screen stays mounted but out of reach:
          no tabbing into it, nothing read out (#80). */}
      <div className="app-under" inert={tabletLocked}>
      {/* The menu: a sidebar, a rail or a bottom bar by width (Nav.tsx). */}
      <Nav
        screens={visibleScreens}
        current={def.kind}
        homeKind={DEFAULT_SCREEN.kind}
        hrefFor={(kind) => locationToUrl({ ...where.current, screen: { kind } })}
        onGo={(kind) => go({ kind })}
        brand={me?.org.name ?? "Bar OS"}
        place={place}
        account={account}
        panel={panel}
        onPanel={setPanel}
        onCollapse={wide ? () => collapse(true) : undefined}
        onExpand={wide && collapsed ? () => collapse(false) : undefined}
      />

      <div className="main">
        <header className="topbar">
          <span className="label topbar-title" style={{ letterSpacing: "0.18em" }}>
            {def.label}
          </span>
          <span className="spacer" />
          {acting && me?.platform && (
            // An operator inside a customer's organisation. Leaving clears
            // the acting org for this tab and reloads as themselves.
            <button
              className="btn sm"
              style={{ borderColor: "var(--accent)" }}
              title={`Acting in ${me?.org.name ?? acting} as a Venox operator`}
              onClick={() => {
                setActingOrg(null);
                // Home is the operator's own organisation, which the address
                // stops naming so the default applies.
                window.location.assign(screenToUrl({ kind: "platform" }));
              }}
            >
              Leave {me?.org.name ?? acting}
            </button>
          )}
          <span className="label topbar-venue">
            {venueState?.venue.name ?? ""}
          </span>
          {onTablet && (
            // Whose name changes go in, and the way to hand over (#80).
            <button className="btn sm topbar-lock" onClick={() => void lockTablet()} title="Lock this tablet">
              <span className="topbar-holder">On bar: {session.email}</span> · Lock
            </button>
          )}
          {/* Tablet and phone: who and where live behind this, not in a sidebar. */}
          <button
            className="btn sm topbar-account"
            aria-label="Account and venue"
            aria-expanded={panel === "account"}
            onClick={() => setPanel(panel === "account" ? null : "account")}
          >
            <Icon name="account" size={18} />
          </button>
        </header>

        {error && (
          <div className="pad">
            <div className="msg err">{error}</div>
          </div>
        )}

        {!state && !error && <div className="pad label">Loading the library…</div>}

        {state && (
          <AppContext.Provider value={state}>
            <Screen key={`${def.kind}:${venueId ?? ""}:${visit}`} params={refused ? {} : (screen.params ?? {})} />
          </AppContext.Provider>
        )}
      </div>
      </div>
      {tabletLocked && (
        <LockScreen
          overlay
          onUnlocked={(u) => unlocked(u, true)}
          onPassword={() => {
            setTabletLocked(false);
            setSession(null);
            setPasswordInstead(true);
          }}
        />
      )}
      {stepUp && <StepUp onClose={() => setStepUp(false)} />}
    </div>
  );
}
